import * as React from "react";
import HelpDialog from "../messages/HelpDialog";
import SubmissionHelpText from "../messages/Help/submission";
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Icon,
    IconButton,
    Paper,
    Stack,
    styled,
    TextField,
    Typography
} from "@mui/material";
import {CloudUpload} from "@mui/icons-material";
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {User} from "../../model";


interface SubmissionMetaDialogProps {
    show: boolean;
    submissionId: string;

    onClose: () => void;

    onSubmissionIdChange: (submissionId: string) => void;
    submissionIdValue: string;

    onPathChange: (path: string) => void;
    pathValue: string;

    onAllowPublicationChange: (allowPublication: boolean) => void;
    allowPublication: boolean;

    onPublicationDateChange: (publicationDate: string | null) => void;
    publicationDate: string | null;

    doi: string;
    onDoiChange: (doi: string) => void;
    requester_email: string;
    onRequesterEmailChange: (requester_email: string) => void;

    onSubmit: () => void;

    onClearForm: () => void;

    helpDialogOpen: boolean;
    openHelpDialog: () => void;
    closeHelpDialog: () => void;

    submissionSucceeded: boolean;

    user: User | null;
}

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));



class SubmissionMetaDialog extends React.Component<SubmissionMetaDialogProps> {
    constructor(props: SubmissionMetaDialogProps) {
        super(props);
    }

    handleSubmissionIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        this.props.onSubmissionIdChange(value);
    };

    handleOnPathChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const path = event.target.value;

        this.props.onPathChange(path);
    };

    handleOnDOIChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const doi = event.target.value;

        this.props.onDoiChange(doi);
    };

    handleOnRequesterEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value;

        this.props.onRequesterEmailChange(email);
    };

    handleAllowPublicationChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        this.props.onAllowPublicationChange(checked);
    };

    handleSubmit = () => {
        this.props.onSubmit();
        if (this.props.submissionSucceeded) {
            this.props.onClearForm();
            this.props.onClose();
        }
    };

    render() {

        const user = this.props.user;
        const isAdmin = user && (user.roles.indexOf('admin') > -1);
        const isAdminDOI = user && (user.roles.indexOf('adminDOI') > -1);

        const doi_trim_low = this.props.doi ? this.props.doi.trim().toLowerCase() : "";
        const doi_is_not_empty = doi_trim_low.length > 0;
        const doi_is_rejected = doi_trim_low.startsWith("reject");
        const doi_is_not_empty_and_not_rejected = doi_is_not_empty && !doi_is_rejected;
        const doi_case_and_not_doi_admin = doi_is_not_empty_and_not_rejected && !isAdminDOI


        return (
            <Dialog
                open={this.props.show}
                onClose={this.props.onClose}
                PaperProps={{sx: {minWidth: 600}}}
            >
            <DialogTitle id="form-dialog-title">
                Update Submission Meta Data
                <IconButton
                    onClick={this.props.openHelpDialog}
                >
                    <Icon color={"secondary"}>
                        help
                    </Icon>
                </IconButton>
                <HelpDialog
                    open={this.props.helpDialogOpen}
                    onClose={this.props.closeHelpDialog}
                    title={'Submission Help'}
                >
                    {SubmissionHelpText}
                </HelpDialog>
            </DialogTitle>
            <DialogContent>
                <Stack direction={'column'} spacing={5}>
                    <Item sx={{ textAlign: "left" }}>
                        <Typography variant="overline" color="text.secondary">
                            Submission Id
                        </Typography>
                        <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                            {this.props.submissionIdValue}
                        </Typography>
                    </Item>

                    <Item sx={{ textAlign: "left" }}>
                        <Typography variant="overline" color="text.secondary">
                            Submission Path
                        </Typography>
                        <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                            {this.props.pathValue}
                        </Typography>
                    </Item>
                    <Item>
                        <DatePicker
                            value={this.props.publicationDate}
                            onChange={this.props.onPublicationDateChange}
                            renderInput={(params) => <TextField {...params} fullWidth helperText={null} />}
                            disabled={doi_case_and_not_doi_admin}
                        />
                    </Item>
                    <Item>
                        <TextField
                            disabled={!isAdminDOI}
                            id="DOI"
                            label="DOI"
                            margin="dense"
                            variant="outlined"
                            value={this.props.doi}
                            onChange={this.handleOnDOIChange}
                            fullWidth
                        />
                    </Item>
                        <Item>
                            <TextField
                                id="RequesterEmail"
                                label="Requester Email"
                                margin="dense"
                                variant="outlined"
                                value={this.props.requester_email}
                                onChange={this.handleOnRequesterEmailChange}
                                fullWidth
                            />
                        </Item>
                    <Item>
                        <FormControlLabel
                            control={<Checkbox
                                value={'publish'}
                                checked={this.props.allowPublication}
                                onChange={this.handleAllowPublicationChange}
                                disabled={doi_case_and_not_doi_admin || doi_is_not_empty}
                            />
                            }
                            label={'Publish Data (Y)'}
                        />
                    </Item>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="contained"
                        color="secondary"
                        onClick={this.handleSubmit}
                >
                    Submit
                    <CloudUpload/>
                </Button>
                <Button variant="contained"
                        color="secondary"
                        onClick={this.props.onClearForm}
                >
                    Clear
                </Button>
                <Button onClick={this.props.onClose}
                        aria-label="Close"
                        variant="contained"
                        color="secondary"
                >
                    Close
                </Button>
            </DialogActions>
            </Dialog>
        );
    }
}

export default SubmissionMetaDialog;
